/**
 * TREATX-LEGAL-REGISTRY-ALL-01
 * ----------------------------
 * Super-Admin "All records" view over the canonical legal corpus.
 * Read-only diagnostics + the single controlled `Reconcile for Agreement
 * Management` action. No second SSOT: every field comes from
 * GET /api/admin/legal/registry/all.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ShieldAlert, Wrench, ChevronDown, ChevronRight, Search,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../utils/api';

const FLAG_LABEL = {
  reconcilable: 'Reconcilable',
  draft_unpublished: 'Draft / unpublished',
  archived: 'Archived',
  import_pending: 'Import pending',
  bootstrap_empty_record: 'Bootstrap / empty record',
  no_version: 'No version',
  historical_evidence: 'Historical evidence exists',
  referenced_by_configuration: 'Referenced by configuration',
  not_eligible_for_agreement_management: 'Not eligible for normal Agreement Management',
  manual_review_required: 'Manual review required',
  internal: 'Internal document',
};

const FLAG_CLS = {
  reconcilable: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  manual_review_required: 'bg-rose-50 text-rose-700 border-rose-200',
  historical_evidence: 'bg-amber-50 text-amber-800 border-amber-200',
  bootstrap_empty_record: 'bg-violet-50 text-violet-700 border-violet-200',
  no_version: 'bg-violet-50 text-violet-700 border-violet-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
  import_pending: 'bg-sky-50 text-sky-700 border-sky-200',
};

const Flag = ({ code }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${FLAG_CLS[code] || 'bg-slate-50 text-slate-600 border-slate-200'}`}
    data-testid={`registry-flag-${code}`}
  >
    {FLAG_LABEL[code] || code}
  </span>
);

const titleOf = (r) => {
  if (typeof r.title === 'string') return r.title;
  return r.title?.en || r.title?.tr || '—';
};

const Stat = ({ label, value, testid }) => (
  <div className="px-3 py-2 rounded-lg border bg-white" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-lg font-semibold text-slate-900">{value}</div>
  </div>
);

const RecordDetail = ({ r }) => (
  <tr className="bg-slate-50/70 border-t" data-testid={`registry-detail-${r.content_key}`}>
    <td colSpan={8} className="px-4 py-3">
      <div className="grid md:grid-cols-3 gap-4 text-xs">
        <div className="space-y-1">
          <div className="font-semibold text-slate-700 mb-1">Why this state</div>
          {(r.visibility_reasons || []).length === 0
            ? <div className="text-emerald-700">Visible and consistent.</div>
            : r.visibility_reasons.map((why) => <div key={why} className="text-slate-600">· {why}</div>)}
          <div className="pt-2 text-slate-500">_id {r._id}</div>
          <div className="text-slate-500">created_by {r.created_by || '—'}</div>
        </div>
        <div className="space-y-1">
          <div className="font-semibold text-slate-700 mb-1">Versions ({r.versions?.length || 0})</div>
          {(r.versions || []).length === 0 && <div className="text-rose-700">No version rows.</div>}
          {(r.versions || []).map((v) => (
            <div key={v.version} className="text-slate-600">
              v{v.version} · {v.publication_state}
              {v.empty_body ? ' · empty body' : ` · ${v.locales.join(', ')}`}
            </div>
          ))}
          <div className="pt-1 text-slate-500">evidence rows: {r.evidence_count}</div>
        </div>
        <div className="space-y-1">
          <div className="font-semibold text-slate-700 mb-1">References</div>
          <div className="text-slate-600">active rules: {r.references?.active_bindings?.join(', ') || '—'}</div>
          <div className="text-slate-600">inactive rules: {r.references?.inactive_bindings?.join(', ') || '—'}</div>
          <div className="text-slate-600">packs: {r.references?.legal_packs?.join(', ') || '—'}</div>
          <div className="text-slate-600">policy sets: {r.references?.policy_sets?.join(', ') || '—'}</div>
          <div className="text-slate-600">consent modules: {r.references?.medical_consent_modules?.join(', ') || '—'}</div>
          <div className="text-slate-600">re-consent rule: {r.references?.reconsent_policy ? 'yes' : 'no'}</div>
        </div>
      </div>

      <div className="mt-3 border-t pt-3">
        {r.reconcile?.already_manageable && (
          <div className="text-xs text-emerald-700">Already managed by Agreement Management.</div>
        )}
        {r.reconcile?.not_applicable && (
          <div className="text-xs text-slate-600" data-testid={`registry-not-applicable-${r.content_key}`}>
            {r.reconcile.reason}
          </div>
        )}
        {r.reconcile?.available && (
          <div className="text-xs" data-testid={`registry-reconcilable-${r.content_key}`}>
            <div className="font-semibold text-emerald-700">
              Reconcilable — use the “Reconcile for Agreement Management” action on this row.
            </div>
            <div className="mt-1 text-slate-500">
              {(r.reconcile.transitions || []).join(' · ')}
            </div>
          </div>
        )}
        {r.reconcile?.manual_review_required && (
          <div className="text-xs" data-testid={`registry-manual-review-${r.content_key}`}>
            <div className="font-semibold text-rose-700 flex items-center gap-1">
              <ShieldAlert className="h-3 w-3"/> Manual review required
            </div>
            <ul className="mt-1 space-y-0.5">
              {(r.reconcile.blockers || []).map((b) => (
                <li key={b.code} className="text-slate-600">· <b>{b.code}</b> — {b.message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </td>
  </tr>
);

export const LegalRegistryAllPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [flagFilter, setFlagFilter] = useState('ALL');
  const [open, setOpen] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/legal/registry/all');
      setData(r.data);
      setError(null);
    } catch (e) {
      const resp = e?.response;
      setError(resp?.data?.detail?.message || resp?.data?.detail || e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const records = useMemo(() => {
    const rows = data?.records || [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (flagFilter === 'HIDDEN' && r.library_visible) return false;
      if (flagFilter === 'VISIBLE' && !r.library_visible) return false;
      if (flagFilter !== 'ALL' && flagFilter !== 'HIDDEN' && flagFilter !== 'VISIBLE'
          && !(r.flags || []).includes(flagFilter)) return false;
      if (!needle) return true;
      return r.content_key.toLowerCase().includes(needle)
        || titleOf(r).toLowerCase().includes(needle)
        || (r.content_type || '').toLowerCase().includes(needle);
    });
  }, [data, q, flagFilter]);

  const reconcile = async (r) => {
    if (!window.confirm(
      `Reconcile “${r.content_key}” for Agreement Management?\n\n`
      + 'The same record is kept (same key, same history). It becomes manageable '
      + 'but stays UNPUBLISHED — you still publish it through the normal wizard.',
    )) return;
    setBusyKey(r.content_key);
    try {
      const res = await api.post(`/admin/legal/registry/${r.content_key}/reconcile`);
      toast.success(`Reconciled — ${r.content_key} is now manageable (still unpublished)`);
      if (res.data?.record?.published) toast.error('Unexpected: record reports published');
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || 'Reconcile failed');
    } finally {
      setBusyKey(null);
    }
  };

  const s = data?.summary;

  return (
    <div data-testid="legal-registry-all-panel">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">All records — Super Admin</h2>
          <p className="text-xs text-slate-500 max-w-2xl">
            Every legal-related record in the canonical store, including drafts, archived,
            import-pending/bootstrap rows, internal documents and records with no version.
            A key that appears here already exists — it cannot be created again.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="registry-reload-btn">
          {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
          <span className="ml-2">Reload</span>
        </Button>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4" data-testid="registry-summary">
          <Stat label="Total" value={s.total} testid="registry-stat-total"/>
          <Stat label="Normal library" value={s.library_visible} testid="registry-stat-visible"/>
          <Stat label="Hidden" value={s.hidden} testid="registry-stat-hidden"/>
          <Stat label="Ghost / no version" value={s.ghost_records} testid="registry-stat-ghosts"/>
          <Stat label="With evidence" value={s.with_evidence} testid="registry-stat-evidence"/>
          <Stat label="Reconcilable" value={s.reconcilable} testid="registry-stat-reconcilable"/>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search key / title / type"
            className="h-8 pl-7 w-64 text-xs"
            data-testid="registry-search"
          />
        </div>
        <select
          value={flagFilter}
          onChange={(e) => setFlagFilter(e.target.value)}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
          data-testid="registry-flag-filter"
        >
          <option value="ALL">All records</option>
          <option value="VISIBLE">Normal library records</option>
          <option value="HIDDEN">Hidden records</option>
          <option value="bootstrap_empty_record">Bootstrap / empty</option>
          <option value="no_version">No version</option>
          <option value="import_pending">Import pending</option>
          <option value="historical_evidence">Historical evidence</option>
          <option value="manual_review_required">Manual review required</option>
          <option value="archived">Archived</option>
          <option value="internal">Internal</option>
        </select>
        <span className="text-xs text-slate-500" data-testid="registry-result-count">
          {records.length} shown
        </span>
      </div>

      {error && (
        <div className="p-4 rounded-xl border border-rose-200 bg-rose-50 text-sm text-rose-800" data-testid="registry-error">
          {String(error)}
        </div>
      )}

      {!error && (
        <div className="bg-white border rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="registry-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Key / Title</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Version</th>
                <th className="text-left px-4 py-2">Locales</th>
                <th className="text-left px-4 py-2">State</th>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-right px-4 py-2">Library / Replace</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && records.map((r) => (
                <React.Fragment key={r.content_key}>
                  <tr
                    className="border-t hover:bg-slate-50 cursor-pointer"
                    onClick={() => setOpen((o) => ({ ...o, [r.content_key]: !o[r.content_key] }))}
                    data-testid={`registry-row-${r.content_key}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 font-medium text-slate-900">
                        {open[r.content_key] ? <ChevronDown className="h-3 w-3"/> : <ChevronRight className="h-3 w-3"/>}
                        {r.content_key}
                      </div>
                      <div className="text-xs text-slate-500 pl-4">{titleOf(r)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.content_type}</td>
                    <td className="px-4 py-3 text-xs">{r.status}</td>
                    <td className="px-4 py-3 text-xs">
                      cv{r.current_version} · {r.published_version_count} published
                    </td>
                    <td className="px-4 py-3 text-xs">{(r.locales || []).join(', ') || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.reconcile?.available && <Flag code="reconcilable"/>}
                        {(r.flags || []).map((f) => <Flag key={f} code={f}/>)}
                      </div>
                    </td>
                    {/* The ONE designed action, surfaced on the row itself so a
                        Super Admin never has to expand a record to discover it.
                        Same handler, same endpoint — no parallel workflow. */}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {r.reconcile?.available ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyKey === r.content_key}
                          onClick={(e) => { e.stopPropagation(); reconcile(r); }}
                          data-testid={`registry-reconcile-btn-${r.content_key}`}
                        >
                          {busyKey === r.content_key
                            ? <Loader2 className="h-3 w-3 animate-spin mr-1"/>
                            : <Wrench className="h-3 w-3 mr-1"/>}
                          Reconcile for Agreement Management
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {r.reconcile?.manual_review_required
                            ? 'Manual review required'
                            : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs">
                      <span className={r.library_visible ? 'text-emerald-700' : 'text-slate-400'}>
                        {r.library_visible ? 'Library' : 'not in Library'}
                      </span>
                      {' / '}
                      <span className={r.replace_visible ? 'text-emerald-700' : 'text-slate-400'}>
                        {r.replace_visible ? 'Replace' : 'not in Replace'}
                      </span>
                    </td>
                  </tr>
                  {open[r.content_key] && (
                    <RecordDetail r={r}/>
                  )}
                </React.Fragment>
              ))}
              {!loading && records.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No records match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LegalRegistryAllPanel;
